import { z } from 'zod';
import { BUSINESS_NATURES, CONTACT_PRIORITIES, PRICING_POLICIES, SOCIAL_KINDS } from '../types';
import { brandedId, emailField, timestampMs, trimmedString, urlOrEmpty } from './_helpers';
export const personaSchema = z.object({
    title: trimmedString(120),
    desc: z.string().trim().max(2000).default(''),
});
export const pricingProductSchema = z.object({
    name: trimmedString(120),
    price: z.string().trim().max(80).default(''),
    note: z.string().trim().max(500).default(''),
});
export const socialLinkSchema = z.object({
    kind: z.enum(SOCIAL_KINDS),
    label: z.string().trim().max(80).default(''),
    url: urlOrEmpty,
});
export const insertBusinessSchema = z.object({
    id: brandedId().optional(),
    userId: brandedId(),
    linkedinAccountId: brandedId().optional(),
    name: trimmedString(120),
    description: z.string().trim().max(4000).default(''),
    website: urlOrEmpty.default(''),
    socials: z.array(socialLinkSchema).max(20).default([]),
    email: z
        .string()
        .trim()
        .max(254)
        .refine((v) => v === '' || emailField.safeParse(v).success, {
        message: 'invalid email',
    })
        .default(''),
    phone: z.string().trim().max(40).default(''),
    calendarUrl: urlOrEmpty.default(''),
    contactPriority: z.enum(CONTACT_PRIORITIES).default('calendar_first'),
    personas: z.array(personaSchema).max(20).default([]),
    pains: z.array(z.string().trim().min(1).max(280)).max(40).default([]),
    valueProps: z.array(z.string().trim().min(1).max(280)).max(40).default([]),
    hotSignals: z.array(z.string().trim().min(1).max(120)).max(40).default([]),
    businessNature: z.enum(BUSINESS_NATURES).default(''),
    pricingModels: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
    pricingProducts: z.array(pricingProductSchema).max(20).default([]),
    pricingPolicy: z.enum(PRICING_POLICIES).default('on_demand'),
    questionGuide: z.array(z.string().trim().min(1).max(280)).max(40).default([]),
    bannedWords: z.array(z.string().trim().min(1).max(120)).max(40).default([]),
    salesRules: z.array(z.string().trim().min(1).max(280)).max(40).default([]),
});
export const selectBusinessSchema = insertBusinessSchema.extend({
    id: brandedId(),
    createdAt: timestampMs,
    updatedAt: timestampMs,
    archivedAt: timestampMs.optional(),
});
export const updateBusinessSchema = insertBusinessSchema.partial().omit({ userId: true });
/**
 * Form schema for the EditBusiness page. Forces a non-empty name + the
 * account-binding decision (EPIC-09 US-119).
 */
export const businessFormSchema = insertBusinessSchema.extend({
    name: trimmedString(120),
    linkedinAccountId: brandedId(),
});
function isHttpsUrlOfKind(value, kind) {
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password)
            return false;
        if (kind === 'website')
            return true;
        const hostname = url.hostname.toLowerCase();
        if (hostname !== 'linkedin.com' && !hostname.endsWith('.linkedin.com'))
            return false;
        const segments = url.pathname.split('/').filter(Boolean);
        return (segments.length >= 2 && segments[0]?.toLowerCase() === (kind === 'company' ? 'company' : 'in'));
    }
    catch {
        return false;
    }
}
/** AI-scan input modal payload (`POST /businesses/:id/ai-scan`). */
export const businessAiScanInputSchema = z
    .object({
    website: urlOrEmpty.optional(),
    linkedinCompany: urlOrEmpty.optional(),
    linkedinProfile: urlOrEmpty.optional(),
    additionalContext: z.string().trim().max(4000).optional(),
    readHistory: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    if (value.website && !isHttpsUrlOfKind(value.website, 'website')) {
        ctx.addIssue({ code: 'custom', path: ['website'], message: 'website must use https' });
    }
    if (value.linkedinCompany && !isHttpsUrlOfKind(value.linkedinCompany, 'company')) {
        ctx.addIssue({
            code: 'custom',
            path: ['linkedinCompany'],
            message: 'expected an https LinkedIn company URL',
        });
    }
    if (value.linkedinProfile && !isHttpsUrlOfKind(value.linkedinProfile, 'profile')) {
        ctx.addIssue({
            code: 'custom',
            path: ['linkedinProfile'],
            message: 'expected an https LinkedIn profile URL',
        });
    }
})
    .refine((v) => Boolean(v.website ||
    v.linkedinCompany ||
    v.linkedinProfile ||
    v.additionalContext ||
    v.readHistory === true), { message: 'at least one source is required' });
/**
 * Strict output contract for the full-business AI scan.
 *
 * Model output is untrusted input. Keep these limits at least as strict as the
 * editable business fields above so a scan can never persist a shape that the
 * editor cannot render or that a later PATCH would reject.
 */
export const businessScanSuggestionSchema = z
    .object({
    description: z.string().trim().min(1).max(4000),
    personas: z
        .array(z.object({
        title: z.string().trim().min(1).max(120),
        desc: z.string().trim().max(2000),
    }))
        .max(12),
    pains: z.array(z.string().trim().min(1).max(280)).max(12),
    valueProps: z.array(z.string().trim().min(1).max(280)).max(12),
    hotSignals: z.array(z.string().trim().min(1).max(120)).max(12),
    questionGuide: z.array(z.string().trim().min(1).max(280)).max(12),
    salesRules: z.array(z.string().trim().min(1).max(280)).max(12),
})
    .strict();
/** AI-scan apply payload — list of fields the user opted in to. */
export const businessAiScanApplySchema = z
    .object({
    acceptedFields: z
        .array(z.enum([
        'description',
        'personas',
        'pains',
        'valueProps',
        'hotSignals',
        'questionGuide',
        'salesRules',
    ]))
        .min(1),
})
    .strict();
//# sourceMappingURL=business.js.map